import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import App from './App';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import APIDocumentationPage from './pages/APIDocumentationPage';
import AuditDashboardPage from './pages/AuditDashboardPage';
import SecureDocumentsPage from './pages/SecureDocumentsPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import CareersPage from './pages/CareersPage';
import ContactPage from './pages/ContactPage';
import { useAuthStore } from './store/authStore';
import { FullPageLoading } from './components/molecules/LoadingStates';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoading message="Authenticating..." />;
  }

  return isAuthenticated ? <>{children}</> : <AuthPage />;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const isAdmin = user?.email === 'richierodney434@gmail.com';

  if (isLoading) {
    return <FullPageLoading message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return isAdmin ? <>{children}</> : <DashboardPage />;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoading message="Loading..." />;
  }

  return isAuthenticated ? <DashboardPage /> : <>{children}</>;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: <PublicRoute><LandingPage /></PublicRoute>
      },
      {
        path: 'auth',
        element: <PublicRoute><AuthPage /></PublicRoute>
      },
      {
        path: 'api-docs',
        element: <APIDocumentationPage />
      },
      {
        path: 'dashboard',
        element: <ProtectedRoute><DashboardPage /></ProtectedRoute>
      },
      {
        path: 'secure-documents',
        element: <ProtectedRoute><SecureDocumentsPage /></ProtectedRoute>
      },
      {
        path: 'audit-dashboard',
        element: <AdminRoute><AuditDashboardPage /></AdminRoute>
      },
      {
        path: 'about',
        element: <AboutPage />
      },
      {
        path: 'blog',
        element: <BlogPage />
      },
      {
        path: 'careers',
        element: <CareersPage />
      },
      {
        path: 'contact',
        element: <ContactPage />
      }
    ]
  }
]);

const Routes: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default Routes;